/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, InputAdornment, Popover, TextField } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useAtom, useAtomValue } from 'jotai';
import { QSTypography } from '../../../../../../shared/components/atoms';
import { customFieldConditionsAtom } from '../../../states/chat-list';
import { customFieldPickerAnchorAtom, customFieldValuePickerAtom } from './state';
import { useFilterMetadata } from './hooks/use-filter-metadata';
import { EntityTypes } from '../../../typings';
import { CustomFieldFieldMetadata, DEFAULT_MAX_CUSTOM_FIELD_FILTER_CONDITIONS } from './typings';
import DataTypeIcon, { dataTypeLabel } from './data-type-icon';
import { countSelectedOptions } from './utils';
import SearchIcon from '../../../../../../shared/icons/icons-revamp/search';
import OverflowTooltip from '../../../../../../shared/components/molecules/overflow-tooltip';
import QSTooltip from '../../../../../../shared/components/molecules/tooltip';

const PAPER_WIDTH = 360;
const PAPER_HEIGHT = 400;

interface FieldRowProps {
  field: CustomFieldFieldMetadata;
  selectedCount: number;
  active: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  onClick: (field: CustomFieldFieldMetadata) => void;
}

const FieldRow = memo(({ field, selectedCount, active, disabled, disabledTooltip, onClick }: FieldRowProps) => {
  const row = (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (disabled) return;
        onClick(field);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') onClick(field);
      }}
      css={css`
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 8px 8px 12px;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        outline: none;
        opacity: ${disabled ? 0.6 : 1};
        background: ${active && !disabled ? '#f7f7f7' : 'transparent'};
        &:hover {
          background: ${disabled ? 'transparent' : '#f7f7f7'};
        }
        &:focus-visible {
          background: ${disabled ? 'transparent' : '#f0f7f4'};
        }
      `}
    >
      <Box
        css={css`
          flex: 1;
          min-width: 0;
        `}
      >
        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 600;
            color: #2d2d2d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          <OverflowTooltip>{`${field.name}`}</OverflowTooltip>
        </QSTypography>
        <Box
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          <DataTypeIcon dataType={field.dataType} size={16} />
          <QSTypography
            css={css`
              font-size: 12px;
              color: #7a7a7a;
              white-space: nowrap;
            `}
          >
            {dataTypeLabel(field.dataType)}
          </QSTypography>
          {field.entityType && (
            <>
              <Box
                css={css`
                  width: 3px;
                  height: 3px;
                  background: #7a7a7a;
                  border-radius: 50%;
                  flex-shrink: 0;
                `}
              />
              <QSTypography
                css={css`
                  font-size: 12px;
                  color: #7a7a7a;
                  white-space: nowrap;
                `}
              >
                {field.entityType === EntityTypes.CHAT ? 'Channel level' : 'Customer level'}
              </QSTypography>
            </>
          )}
        </Box>
      </Box>
      {selectedCount > 0 && (
        <Box
          css={css`
            background: #eaf3f0;
            color: #3f906d;
            font-size: 12px;
            font-weight: 600;
            padding: 4px 10px;
            border-radius: 12px;
            white-space: nowrap;
          `}
        >
          {selectedCount}
        </Box>
      )}
      {!disabled && <ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#7a7a7a' }} />}
    </Box>
  );

  if (disabled && disabledTooltip) {
    return (
      <QSTooltip placement="top" title={disabledTooltip}>
        <span
          css={css`
            display: block;
          `}
        >
          {row}
        </span>
      </QSTooltip>
    );
  }
  return row;
});
FieldRow.displayName = 'FieldRow';

const FieldPickerPopover = () => {
  const [anchor, setAnchor] = useAtom(customFieldPickerAnchorAtom);
  const [valuePicker, setValuePicker] = useAtom(customFieldValuePickerAtom);
  const conditions = useAtomValue(customFieldConditionsAtom);
  const { requestData: metadataRequest } = useFilterMetadata(true);

  const [search, setSearch] = useState('');
  const [paperEl, setPaperEl] = useState<HTMLElement | null>(null);

  const selectedCountByFieldId = useMemo(() => {
    const m = new Map<string, number>();
    conditions.forEach((c) => {
      m.set(c.id, (m.get(c.id) ?? 0) + countSelectedOptions(c));
    });
    return m;
  }, [conditions]);

  const maxConditions =
    metadataRequest.data?.maxCustomFieldFilterConditions ?? DEFAULT_MAX_CUSTOM_FIELD_FILTER_CONDITIONS;
  const distinctFieldsApplied = useMemo(() => new Set(conditions.map((c) => c.id)).size, [conditions]);
  const limitReached = distinctFieldsApplied >= maxConditions;
  const disabledTooltip = `You can only filter values across ${maxConditions} custom fields at a time`;

  const visibleFields = useMemo(() => {
    const fields = (metadataRequest.data?.fields ?? []).filter((f) => f.filterable);
    const q = search.trim().toLowerCase();
    const filtered = q ? fields.filter((f) => f.name.toLowerCase().includes(q)) : fields;
    return [...filtered].sort((a, b) => {
      const aSelected = (selectedCountByFieldId.get(a.id) ?? 0) > 0 ? 1 : 0;
      const bSelected = (selectedCountByFieldId.get(b.id) ?? 0) > 0 ? 1 : 0;
      return bSelected - aSelected;
    });
  }, [metadataRequest.data?.fields, search, selectedCountByFieldId]);

  const handleClose = () => {
    setAnchor(null);
    setSearch('');
  };

  const handleFieldClick = (field: CustomFieldFieldMetadata) => {
    if (!paperEl) return;
    const existingIndex = conditions.findIndex((c) => c.id === field.id);
    setValuePicker({ anchor: paperEl, fieldId: field.id, editIndex: existingIndex });
  };

  const isLoading = metadataRequest.loading && !metadataRequest.data;
  const hasError = Boolean(metadataRequest.error) && !metadataRequest.data;

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        ref: (node: HTMLElement | null) => setPaperEl(node),
        sx: {
          width: PAPER_WIDTH,
          height: PAPER_HEIGHT,
          borderRadius: '8px',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid rgba(30, 41, 59, 0.14)',
        },
      }}
    >
      <Box
        css={css`
          padding: 4px 0px;
          background: #ffffff;
        `}
      >
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={'Search custom field name'}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={16} color="#7a7a7a" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '6px',
              fontSize: 14,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none !important' },
              '&.Mui-focused fieldset': { border: 'none !important' },
            },
          }}
        />
      </Box>
      <Divider />
      <Box
        css={css`
          overflow-y: auto;
          flex: 1;
        `}
      >
        {isLoading && (
          <Box
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 32px;
            `}
          >
            <CircularProgress size={20} sx={{ color: '#3f906d' }} />
          </Box>
        )}
        {hasError && (
          <QSTypography
            css={css`
              padding: 24px 16px;
              text-align: center;
              color: #b00020;
              font-size: 13px;
            `}
          >
            Could not load custom fields. Please try again.
          </QSTypography>
        )}
        {!isLoading && !hasError && visibleFields.length === 0 && (
          <Box
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              padding: 24px 16px;
            `}
          >
            <QSTypography
              css={css`
                text-align: center;
                color: #7a7a7a;
                font-size: 13px;
              `}
            >
              {search.trim().length > 0 ? `No results found for "${search.trim()}"` : 'No custom fields available'}
            </QSTypography>
          </Box>
        )}
        {!isLoading &&
          !hasError &&
          visibleFields.map((field) => {
            const alreadyApplied = (selectedCountByFieldId.get(field.id) ?? 0) > 0;
            const rowDisabled = limitReached && !alreadyApplied;
            return (
              <FieldRow
                key={field.id}
                field={field}
                selectedCount={selectedCountByFieldId.get(field.id) ?? 0}
                active={valuePicker?.fieldId === field.id}
                disabled={rowDisabled}
                disabledTooltip={rowDisabled ? disabledTooltip : undefined}
                onClick={handleFieldClick}
              />
            );
          })}
      </Box>
    </Popover>
  );
};

export default memo(FieldPickerPopover);
